import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Box, IconButton, Typography } from '@mui/material'
import { message, Table } from 'antd'
import dayjs from 'dayjs'
import { useContext, useState } from 'react'

import InventoryIcon from '@mui/icons-material/Inventory'
import LinkIcon from '@mui/icons-material/Link'
import { RiFileCopyFill } from 'react-icons/ri'
import { AbilityContext, isHead } from '../../../components/Ability'
import ConfirmModal from '../../../components/ConfirmModal'
import ImgTag from '../../../components/ImgTag'
import Pagination from '../../../components/Pagination'
import { Tag } from '../../../components/Tag'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { ONLY_DATE_FORMAT, ONLY_TIME_FORMAT } from '../../../constants/global'
import { isAllowShopee, isAllowTikTok } from '../../../constants/user'
import { axiosInstance } from '../../../hooks/useAxios'
import { useUser } from '../../../hooks/useUser'
import { IProduct, IProductCategory } from '../../../types/product'
import { IFilterMapped } from './ProductFilter'
import { ProductFormModal } from './ProductFormModal'
import { ProductPlatformLinksModal } from './ProductPlatformLinksModal'
import { ProductStockFormModal } from './ProductStockFormModal'

interface IProductsTableProps {
  isLoading: boolean
  data: IProduct[]
  onUpdateProductSuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
}

const ProductsTable = ({
  data,
  onUpdateProductSuccess,
  filter,
  onFilterChange,
  handlePageChange,
  total,
}: IProductsTableProps) => {
  const ability = useContext(AbilityContext)
  const user = useUser()
  const [isModalEditProduct, setIsModalEditProduct] = useState<boolean>(false)
  const [isModalStockProduct, setIsModalStockProduct] = useState<boolean>(false)
  const [isModalPlatformLinks, setIsModalPlatformLinks] =
    useState<boolean>(false)
  const [productEdit, setProductEdit] = useState<
    IProduct & { isCopy?: boolean }
  >()
  const [isModalDeleteProduct, setIsModalDeleteProduct] =
    useState<boolean>(false)

  const onHandleDeleteProduct = async (objectId: string) => {
    try {
      const response = await axiosInstance.delete(`/product/${objectId}`)
      if (response.status === 200) {
        message.success('ลบข้อมูลสำเร็จ')
        onUpdateProductSuccess()
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'name',
      key: 'name',
      align: 'center' as const,
      render: (name: string, product: IProduct) => {
        return (
          <Box
            sx={{
              minWidth: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
          >
            <ImgTag imageUrl={product.image} name={name} />
            <Box ml={1}>
              <Typography variant="h6" color="text.secondary">
                {name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                รหัสสินค้า : {product?.code}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    // {
    //   title: 'ช่องทางการขาย',
    //   dataIndex: 'platform',
    //   key: 'platform',
    //   align: 'center' as const,
    //   render: (platform: string[]) => {
    //     return (
    //       <Box
    //         sx={{ minWidth: 70, flexWrap: 'wrap', width: 100 }}
    //         display="flex"
    //         justifyContent="center"
    //         alignItems="center"
    //       >
    //         {platform?.map(
    //           (name: string) =>
    //             name && (
    //               <Box key={name} mr={0.4} mb={0.4}>
    //                 <ImgTag
    //                   imageUrl={
    //                     platformMapImage[name as keyof typeof platformMapImage]
    //                   }
    //                   name={name}
    //                   size={30}
    //                 />
    //               </Box>
    //             ),
    //         )}
    //       </Box>
    //     )
    //   },
    // },
    {
      title: 'หมวดหมู่',
      dataIndex: 'productCategory',
      key: 'productCategory',
      align: 'center' as const,
      render: (productCategory: IProductCategory) => {
        return (
          <Box sx={{ minWidth: 70 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {productCategory?.name ?? '-'}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center' as const,
      render: (quantity: number, product: IProduct) => {
        return (
          <Box sx={{ minWidth: 70 }}>
            {!product.isCollection ? (
              <Typography variant="subtitle2" color="text.secondary">
                {quantity ? quantity.toLocaleString() : 0}
              </Typography>
            ) : (
              <Typography variant="subtitle2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      title: 'ค่าคอมสินค้า',
      dataIndex: 'commission',
      key: 'commission',
      align: 'center' as const,
      render: (commission: number) => {
        return (
          <Box sx={{ minWidth: 80 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {commission ? commission.toLocaleString() : 0}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ราคา',
      dataIndex: 'price',
      key: 'price',
      align: 'center' as const,
      render: (price: number) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {price ? price.toLocaleString() : 0}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ต้นทุน',
      dataIndex: 'cost',
      key: 'cost',
      align: 'center' as const,
      render: (cost: number) => {
        return (
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {cost.toLocaleString()}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      align: 'center' as const,
      render: (updatedAt: string) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(updatedAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'isCollection',
      key: 'isCollection',
      align: 'center' as const,
      render: (isCollection: string, product: IProduct) => {
        return (
          <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={1}>
            {/* <Typography variant="subtitle2" color="text.secondary">
              {description?.length > 0 ? description : <em>ไม่ระบุ</em>}
            </Typography> */}
            <Tag
              color={product.isAvailable ? 'success' : 'error'}
              style={{ margin: 0 }}
            >
              {product.isAvailable ? 'เปิดใช้งาน' : 'ระงับการใช้งาน'}
            </Tag>
            {isCollection && (
              <Tag color={isCollection?.length! > 0 ? 'primary' : 'warning'}>
                สินค้าแบบเซ็ต
              </Tag>
            )}
          </Box>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: undefined, product: IProduct) => {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {(ability.can('manage', 'Product', 'reportProduct') ||
              isHead(user.user as any)) &&
              !product.isCollection && (
                <IconButton
                  title={'จัดการสต๊อกสินค้า'}
                  onClick={() => {
                    setProductEdit(product)
                    setIsModalStockProduct(true)
                  }}
                >
                  <InventoryIcon />
                </IconButton>
              )}
            <IconButton
              title={'แก้ไขสินค้า'}
              onClick={() => {
                setProductEdit(product)
                setIsModalEditProduct(true)
              }}
            >
              <EditIcon />
            </IconButton>
            {(isAllowTikTok(user.merchant) || isAllowShopee(user.merchant)) && (
              <IconButton
                title={'เชื่อมสินค้ากับ Platform'}
                color={product.platformLinks?.length ? 'primary' : 'default'}
                onClick={() => {
                  setProductEdit(product)
                  setIsModalPlatformLinks(true)
                }}
              >
                <LinkIcon />
              </IconButton>
            )}
            <IconButton
              title={'คัดลอกสินค้า'}
              onClick={() => {
                setProductEdit({
                  ...product,
                  quantity: 0,
                  code: '',
                  objectId: '',
                  isCopy: true,
                })
                setIsModalEditProduct(true)
              }}
            >
              <RiFileCopyFill />
            </IconButton>
            {/* <IconButton
              title={'ลบสินค้า'}
              onClick={() => {
                setProductEdit(product)
                setIsModalDeleteProduct(true)
              }}
            >
              <DeleteIcon />
            </IconButton> */}
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <ConfirmModal
        open={isModalDeleteProduct}
        title={'ลบสินค้า'}
        message={`คุณต้องการลบสินค้า ${productEdit?.name} ใช่หรือไม่`}
        onClose={() => setIsModalDeleteProduct(false)}
        onConfirm={() => {
          onHandleDeleteProduct(productEdit!.objectId!)
          setIsModalDeleteProduct(false)
        }}
      />
      <ProductFormModal
        isOpen={isModalEditProduct}
        onUpdateProductSuccess={onUpdateProductSuccess}
        onClose={(visible) => setIsModalEditProduct(visible)}
        product={productEdit}
      />
      {isModalStockProduct && (
        <ProductStockFormModal
          onUpdateProductSuccess={onUpdateProductSuccess}
          isOpen={isModalStockProduct}
          onClose={(visible) => setIsModalStockProduct(visible)}
          product={productEdit!}
        />
      )}
      {isModalPlatformLinks && (
        <ProductPlatformLinksModal
          onUpdateProductSuccess={onUpdateProductSuccess}
          isOpen={isModalPlatformLinks}
          onClose={(visible) => setIsModalPlatformLinks(visible)}
          product={productEdit!}
        />
      )}
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((product: IProduct, index: number) => {
          return {
            ...product,
            key: product.objectId,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default ProductsTable
